import {IAsyncData, IScheduleRotation, IShiftDetail, IWeek, WeekDays} from '@signal/asp-data-commons';
import produce from 'immer';
import * as fromActions from './schedule-rotation.actions';
import * as fromAdapter from './schedule-rotation.adapter';

export interface IScheduleRotationLabelId {
  activeId: string;
  upcomingId: string;
  inactiveIds: string[];
  invalidIds: string[];
}
export interface IScheduleRotations {
  [key: string]: IScheduleRotation;
}

export interface ScheduleRotationState {
  service: IAsyncData<{ [key: string]: IScheduleRotations }>;
  sales: IAsyncData<{ [key: string]: IScheduleRotations }>;
}

const initialShiftDetail: IShiftDetail[] = [{
  shiftId: '',
  shiftName: '',
  detail: [{
    id: '',
    name: ''
  }]
}];

const initialWeekData: IWeek = {
  [WeekDays.MONDAY]: initialShiftDetail,
  [WeekDays.TUESDAY]: initialShiftDetail,
  [WeekDays.WEDNESDAY]: initialShiftDetail,
  [WeekDays.THURSDAY]: initialShiftDetail,
  [WeekDays.FRIDAY]: initialShiftDetail,
  [WeekDays.SATURDAY]: initialShiftDetail,
  [WeekDays.SUNDAY]: initialShiftDetail,
};

const initialServiceData = {
  data: {},
  loading: false,
  loaded: false,
  error: ''
};

const initialSalesData = {
  data: {},
  loading: false,
  loaded: false,
  error: ''
};

const initialState: ScheduleRotationState = {
  service: initialServiceData,
  sales: initialSalesData
};

export function scheduleRotationReducer(
  state: ScheduleRotationState = initialState,
  action: fromActions.ScheduleRotationActions): ScheduleRotationState {

  switch (action.type) {
    case fromActions.ScheduleRotationActionTypes.LOAD_SCHEDULE_ROTATION: {
      return produce(state, draftState => {
        const department = action instanceof fromActions.LoadScheduleRotation ? action.payload.department?.toLowerCase() : '';
        draftState[department].loaded = false;
        draftState[department].loading = true;
        draftState[department].error = '';
      });
    }

    case fromActions.ScheduleRotationActionTypes.LOAD_SCHEDULE_ROTATION_SUCCESS: {
      return produce(state, draftState => {
        const scheduleRotation = action instanceof fromActions.LoadScheduleRotationSuccess
          ? action.payload?.scheduleRotation : undefined;
        const department = action.payload.department?.toLowerCase();
        draftState[department].data = scheduleRotation;
        draftState[department].loaded = true;
        draftState[department].loading = false;
        draftState[department].error = '';
      });
    }

    case fromActions.ScheduleRotationActionTypes.LOAD_SCHEDULE_ROTATION_FAILURE: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadScheduleRotationFailure ? action.payload : undefined;
        const department = payload?.department?.toLowerCase();
        draftState[department].loaded = false;
        draftState[department].loading = false;
        draftState[department].error = payload.error;
      });
    }

    case fromActions.ScheduleRotationActionTypes.ADD_SCHEDULE_ROTATION: {
      return produce(state, draftState => {
        const department = action instanceof fromActions.AddScheduleRotation ? action.payload.department?.toLowerCase() : '';
        draftState[department].loaded = false;
        draftState[department].loading = true;
        draftState[department].error = '';
      });
    }

    case fromActions.ScheduleRotationActionTypes.ADD_SCHEDULE_ROTATION_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.AddScheduleRotationSuccess ? action.payload : undefined;
        const department = payload.department?.toLowerCase();
        draftState[department].data[payload.scheduleRotation.id] = payload.scheduleRotation;
        draftState[department].loaded = true;
        draftState[department].loading = false;
        draftState[department].error = '';
      });
    }

    case fromActions.ScheduleRotationActionTypes.ADD_SCHEDULE_ROTATION_FAILURE: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.AddScheduleRotationFailure ? action.payload : undefined;
        const department = payload.department?.toLowerCase();
        draftState[department].loaded = true;
        draftState[department].loading = false;
        draftState[department].error = payload.error;
      });
    }

    case fromActions.ScheduleRotationActionTypes.UPDATE_SCHEDULE_ROTATION_NAME: {
      return produce(state, draftState => {
        const department = action instanceof fromActions.UpdateScheduleRotationName ? action.payload.department?.toLowerCase() : '';
        draftState[department].loaded = false;
        draftState[department].loading = true;
        draftState[department].error = '';
      });
    }

    case fromActions.ScheduleRotationActionTypes.UPDATE_SCHEDULE_ROTATION_NAME_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.UpdateScheduleRotationNameSuccess ? action.payload : undefined;
        const department = payload.department?.toLowerCase();
        draftState[department].data[payload.scheduleRotation.id] = payload.scheduleRotation;
        draftState[department].loaded = true;
        draftState[department].loading = false;
        draftState[department].error = '';
      });
    }

    case fromActions.ScheduleRotationActionTypes.UPDATE_SCHEDULE_ROTATION_NAME_FAILURE: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.UpdateScheduleRotationNameFailure ? action.payload : undefined;
        const department = payload.department?.toLowerCase();
        draftState[department].loaded = true;
        draftState[department].loading = false;
        draftState[department].error = payload.error;
      });
    }

    case fromActions.ScheduleRotationActionTypes.DELETE_SCHEDULE_ROTATION: {
      return produce(state, draftState => {
        const department = action instanceof fromActions.DeleteScheduleRotation ? action.payload.department?.toLowerCase() : '';
        draftState[department].loaded = false;
        draftState[department].loading = true;
        draftState[department].error = '';
      });
    }

    case fromActions.ScheduleRotationActionTypes.DELETE_SCHEDULE_ROTATION_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.DeleteScheduleRotationSuccess ? action.payload : undefined;
        const department = payload.department?.toLowerCase();
        delete draftState[department].data[payload.scheduleRotation.id];
        draftState[department].loaded = true;
        draftState[department].loading = false;
        draftState[department].error = '';
      });
    }

    case fromActions.ScheduleRotationActionTypes.DELETE_SCHEDULE_ROTATION_FAILURE: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.DeleteScheduleRotationFailure ? action.payload : undefined;
        const department = payload.department?.toLowerCase();
        draftState[department].loaded = true;
        draftState[department].loading = false;
        draftState[department].error = payload.error;
      });
    }

    case fromActions.ScheduleRotationActionTypes.UPDATE_START_DATE: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.UpdateStartDate ? action.payload : undefined;
        const department = payload?.department?.toLowerCase();
        draftState[department].data[payload.id].startDate = payload?.startDate;
      });
    }

    case fromActions.ScheduleRotationActionTypes.UPDATE_SCHEDULE_ROTATION: {
      let rotationCopy;
      return produce(state, draftState => {
        const payload = action instanceof fromActions.UpdateScheduleRotation ? action.payload : undefined;
        const department = payload?.department?.toLowerCase();
        const index = payload?.index;
        const day = payload?.day;
        const rotation = state[department].data[payload.id].rotation[index][day] as IShiftDetail[];
        const shiftIndex = rotation.findIndex(r=> r.shiftId === payload.shiftId);
        const oldShift = rotation[shiftIndex];
        rotationCopy = [...rotation];
        rotationCopy.splice(shiftIndex, 1,{...oldShift, detail: payload?.weeklyRotations});
        draftState[department].data[payload.id].rotation[index][day] = rotationCopy;
        /*rotation.forEach(r => {
          if(r.shiftId === payload.shiftId){
            r.detail = payload?.weeklyRotations;
          }
        });*/
      });
    }

    case fromActions.ScheduleRotationActionTypes.SAVE_SCHEDULE_ROTATION: {
      return produce(state, draftState => {
        const department = action instanceof fromActions.SaveScheduleRotation ? action.payload.department?.toLowerCase() : '';
        draftState[department].loading = true;
      });
    }

    case fromActions.ScheduleRotationActionTypes.SAVE_SCHEDULE_ROTATION_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.SaveScheduleRotationSuccess
          ? action.payload : undefined;
        const department = payload?.department?.toLowerCase();
        draftState[department].data[payload.scheduleRotation.id] = payload.scheduleRotation
        draftState[department].loaded = true;
        draftState[department].loading = false;
        draftState[department].error = '';
      });
    }

    case fromActions.ScheduleRotationActionTypes.SAVE_SCHEDULE_ROTATION_FAILURE: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.SaveScheduleRotationFailure ? action.payload : undefined;
        const department = payload?.department?.toLowerCase();
        draftState[department].loaded = false;
        draftState[department].loading = false;
        draftState[department].error = payload.error;
      });
    }

    case fromActions.ScheduleRotationActionTypes.ADD_WEEK: {
      return produce(state, draftState => {
        fromAdapter.addWeek(action, draftState, state);
      });
    }

    case fromActions.ScheduleRotationActionTypes.DELETE_WEEK: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.DeleteWeek ? action.payload : undefined;
        const department = payload.department?.toLowerCase();
        (draftState[department].data[action.payload.id] as IScheduleRotation).rotation.splice(payload.weekIndex, 1);
      });
    }

    case fromActions.ScheduleRotationActionTypes.ADD_SHIFT: {
      return produce(state, draftState => {
        fromAdapter.addShift(action, draftState, state);
      });
    }

    case fromActions.ScheduleRotationActionTypes.UPDATE_SHIFT: {
      return produce(state, draftState => {
        fromAdapter.updateShift(action, draftState, state);
      });
    }

    case fromActions.ScheduleRotationActionTypes.DELETE_SHIFT: {
      return produce(state, draftState => {
        fromAdapter.deleteShift(action, draftState, state);
      });
    }

    default:
      return state;
  }
}
